import { useEffect, useState, useCallback, useRef } from 'react';

export interface ProgressUpdate {
  step: 'context_gathering' | 'prompt_building' | 'prompt_optimization' | 'ai_generation' | 'validation' | 'persistence';
  status: 'started' | 'in_progress' | 'completed' | 'failed' | 'info' | 'warning' | 'error';
  message: string;
  details?: Record<string, any>;
  timestamp: string;
  progress_percentage?: number;
}

interface UseProgressStreamOptions {
  onUpdate?: (update: ProgressUpdate) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export function useProgressStream(
  projectId: string | null,
  enabled: boolean = true,
  options: UseProgressStreamOptions = {}
) {
  const [updates, setUpdates] = useState<ProgressUpdate[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Store callbacks in refs to avoid re-creating connect function
  const onUpdateRef = useRef(options.onUpdate);
  const onCompleteRef = useRef(options.onComplete);
  const onErrorRef = useRef(options.onError);

  // Keep refs up to date
  useEffect(() => {
    onUpdateRef.current = options.onUpdate;
    onCompleteRef.current = options.onComplete;
    onErrorRef.current = options.onError;
  }, [options.onUpdate, options.onComplete, options.onError]);

  const connect = useCallback(() => {
    if (!projectId || !enabled) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Clear previous updates
    setUpdates([]);
    setError(null);

    const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
    const url = `${apiUrl}/schema-design/cases/${projectId}/schema/progress`;

    console.log('🔌 Connecting to progress stream:', url);

    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      console.log('✅ Progress stream connected');
      setIsConnected(true);
      setError(null);
    };

    eventSource.onmessage = (event) => {
      try {
        const update: ProgressUpdate = JSON.parse(event.data);
        console.log('📊 Progress update:', update);

        setUpdates((prev) => [...prev, update]);

        // Call onUpdate callback
        if (onUpdateRef.current) {
          onUpdateRef.current(update);
        }

        // Check for completion
        if (update.status === 'completed' && update.step === 'persistence') {
          console.log('✨ Schema generation complete!');
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
          // Close connection after completion
          eventSource.close();
          setIsConnected(false);
        }

        // Check for failure
        if (update.status === 'failed') {
          console.error('❌ Schema generation failed:', update.message);
          const err = new Error(update.message);
          setError(err);
          if (onErrorRef.current) {
            onErrorRef.current(err);
          }
          // Close connection after failure
          eventSource.close();
          setIsConnected(false);
        }
      } catch (err) {
        console.error('Failed to parse progress update:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('❌ Progress stream error:', err);
      const error = new Error('Connection to progress stream lost');
      setError(error);
      setIsConnected(false);

      if (onErrorRef.current) {
        onErrorRef.current(error);
      }

      // Close the connection
      eventSource.close();
    };

    return () => {
      eventSource.close();
      setIsConnected(false);
    };
  }, [projectId, enabled]);

  useEffect(() => {
    if (enabled && projectId) {
      const cleanup = connect();
      return cleanup;
    }
  }, [enabled, projectId, connect]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    }
  }, []);

  const reset = useCallback(() => {
    disconnect();
    setUpdates([]);
    setError(null);
  }, [disconnect]);

  // Get latest update for each step
  const getLatestUpdateForStep = useCallback((step: ProgressUpdate['step']) => {
    return updates.filter(u => u.step === step).pop();
  }, [updates]);

  // Get current progress percentage
  const currentProgress = updates.length > 0
    ? updates[updates.length - 1].progress_percentage || 0
    : 0;

  return {
    updates,
    isConnected,
    error,
    currentProgress,
    connect,
    disconnect,
    reset,
    getLatestUpdateForStep,
  };
}
